import React from "react";
import dynamic from "next/dynamic";

import { City } from "@/custom-types/CitiesNearMe";
import { CountryCode } from "@/custom-types/CountryCode";
import { CopyBlock as CopyBlockType, FAQ } from "@/custom-types/Directus";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { Location } from "@/custom-types/Location";
import { RetailType } from "@/custom-types/Store";
import { useSelector } from "@/redux/hooks";
import { getShowBanner } from "@/redux/selectors/header";

import CopyBlock from "@/components/CopyBlock/CopyBlock";
import ContentLayout from "@/components/dispensaries/ContentSection/ContentLayout";
import RecentlyViewedCarouselWithData from "@/components/dispensaries/RecentlyViewedCarousel";
import RetailCategoryNavigation from "@/components/dispensaries/RetailCategoryNavigation";
import StoreBreadcrumbs from "@/components/dispensaries/StoreBreadcrumbs";
import StoreMenuContainer from "@/components/dispensaries/StoreMenuContainer/StoreMenuContainer";
import FAQs from "@/components/FAQ";

import { StoreLocatorResultsResponse } from "../../../api/requests/consumerApi/getStoreLocatorResults";
import PageNavigation from "../../PageNavigation";
import CitiesNearby from "../CitiesNearby/CitiesNearby";
import Footer from "../Footer/Footer";
import { ContentLayoutProps } from "./ContentLayout/ContentLayout";

const CBDStoresCarousel = dynamic(
  () => import("../CBDStoresCarousel/CBDStoresCarousel"),
  { ssr: true },
);

interface ContentSectionProps {
  cbdStores: DispensaryV3[];
  cities: City[];
  copyBlockData: CopyBlockType | null;
  faqs: FAQ | null;
  isLocationPage: boolean;
  page: number;
  pageLocation: Location;
  retailType: RetailType;
  storeLocatorResults: StoreLocatorResultsResponse;
  showDeliveryValidation: boolean;
  totalPages: number;
  countryCode: CountryCode;
  deliveryFilterSelected: boolean;
}

const ContentSection = ({
  cbdStores,
  cities,
  copyBlockData,
  faqs,
  deliveryFilterSelected,
  isLocationPage,
  page,
  pageLocation,
  retailType,
  showDeliveryValidation,
  storeLocatorResults,
  totalPages,
  countryCode,
}: ContentSectionProps) => {
  const showBanner = useSelector(getShowBanner);
  const product = {
    "cbd-store": "hemp / CBD",
    clinic: "MMJ Doctors",
    dispensary: "weed",
    undefined: "weed",
  };

  return (
    <ContentLayout showBanner={showBanner}>
      {({ toggleFilterList, showMapButton }: ContentLayoutProps) => (
        <div className="flex flex-col h-full bg-white gap-md md:gap-0">
          <div
            className="pt-[10px] md:hidden flex justify-center items-center bg-white"
            data-testid="listing-wrapper-handle"
          >
            <span className="bg-light-grey rounded-full w-[64px] h-[6px]" />
          </div>
          <StoreMenuContainer
            showMapButton={showMapButton}
            storeLocatorResults={storeLocatorResults}
            toggleFilterList={toggleFilterList}
            retailType={retailType}
            pageLocation={pageLocation}
            showDeliveryValidation={showDeliveryValidation}
            showingCBDTreatment={cbdStores.length > 0}
            deliveryFilterSelected={deliveryFilterSelected}
          >
            <PageNavigation currentPage={page} totalPages={totalPages} />
            <RecentlyViewedCarouselWithData />
            {cbdStores.length === 0 ? null : (
              <CBDStoresCarousel
                cbdStores={cbdStores}
                pageLocation={pageLocation}
              />
            )}
            <RetailCategoryNavigation retailType={retailType} />
            <CitiesNearby
              cities={cities}
              heading={`Find ${
                product[retailType as keyof typeof product]
              } in a city near you`}
              retailType={retailType}
            />
            <hr className="border-light-grey" />
            {copyBlockData?.copy_block?.length ? (
              <CopyBlock data={copyBlockData} />
            ) : (
              <></>
            )}
            <FAQs
              data={faqs?.faqs}
              initialShowPanel={true}
              className="bg-green-20 flex flex-col gap-lg py-xxl [&_div]:mb-0 !m-0"
            />
            <StoreBreadcrumbs
              isLocationPage={isLocationPage}
              pageLocation={pageLocation}
              countryCode={countryCode}
              retailType={retailType}
            />
            <Footer className="!px-0" />
          </StoreMenuContainer>
        </div>
      )}
    </ContentLayout>
  );
};

export default ContentSection;
